import { useTranslation } from 'react-i18next';

export type CloseCategory =
  | 'COMPETITIVE_BUSINESS'
  | 'PROCESS_TIMING'
  | 'DATA_QUALITY';

export interface CloseReason {
  code: string;
  category: CloseCategory;
}

/**
 * Epic 86 / ADR-008 — Reason taxonomy for opportunity-LOST and
 * lead-DISCARDED closes. Three categories with distinct metric
 * semantics:
 *   COMPETITIVE_BUSINESS — counts in win-rate
 *   PROCESS_TIMING       — counts but reportable separately
 *   DATA_QUALITY         — auto-excludes from metrics; manager+ only
 */
export const REASONS: ReadonlyArray<CloseReason> = [
  { code: 'LOST_TO_COMPETITOR', category: 'COMPETITIVE_BUSINESS' },
  { code: 'BUDGET_CUT', category: 'COMPETITIVE_BUSINESS' },
  { code: 'NO_DECISION', category: 'COMPETITIVE_BUSINESS' },
  { code: 'WRONG_FIT', category: 'COMPETITIVE_BUSINESS' },
  { code: 'PRICE_TOO_HIGH', category: 'COMPETITIVE_BUSINESS' },
  { code: 'INTERNAL_BUILD', category: 'COMPETITIVE_BUSINESS' },
  { code: 'CHAMPION_LEFT', category: 'COMPETITIVE_BUSINESS' },
  { code: 'NO_RESPONSE', category: 'PROCESS_TIMING' },
  { code: 'DISQUALIFIED', category: 'PROCESS_TIMING' },
  { code: 'ON_HOLD_INDEFINITE', category: 'PROCESS_TIMING' },
  { code: 'LOST_TO_INACTION', category: 'PROCESS_TIMING' },
  { code: 'DUPLICATE', category: 'DATA_QUALITY' },
  { code: 'TEST_DATA', category: 'DATA_QUALITY' },
  { code: 'MISCAPTURE', category: 'DATA_QUALITY' },
  { code: 'WRONG_TENANT', category: 'DATA_QUALITY' },
  { code: 'SPAM_OR_BOT', category: 'DATA_QUALITY' },
];

interface Props {
  value: string;
  onChange: (code: string, category: CloseCategory) => void;
  /**
   * When false (default), DATA_QUALITY codes are disabled with a
   * tooltip indicating manager+ permission. Set true once the caller
   * has confirmed `permissions.has('opp.close.data_quality')`.
   */
  allowDataQuality?: boolean;
  className?: string;
}

export function CloseReasonPicker({
  value,
  onChange,
  allowDataQuality = false,
  className,
}: Props) {
  const { t } = useTranslation();

  const groups: Record<CloseCategory, CloseReason[]> = {
    COMPETITIVE_BUSINESS: [],
    PROCESS_TIMING: [],
    DATA_QUALITY: [],
  };
  REASONS.forEach((r) => groups[r.category].push(r));

  return (
    <select
      className={className ?? 'w-full rounded-md border bg-background px-3 py-2 text-sm'}
      value={value}
      onChange={(e) => {
        const reason = REASONS.find((r) => r.code === e.target.value);
        if (reason) onChange(reason.code, reason.category);
      }}
    >
      <option value="">{t('closeReason.placeholder')}</option>
      <optgroup label={t('closeReason.cat_competitive')}>
        {groups.COMPETITIVE_BUSINESS.map((r) => (
          <option key={r.code} value={r.code}>
            {t(`closeReason.code_${r.code.toLowerCase()}`)}
          </option>
        ))}
      </optgroup>
      <optgroup label={t('closeReason.cat_process')}>
        {groups.PROCESS_TIMING.map((r) => (
          <option key={r.code} value={r.code}>
            {t(`closeReason.code_${r.code.toLowerCase()}`)}
          </option>
        ))}
      </optgroup>
      <optgroup
        label={
          t('closeReason.cat_data_quality') +
          (allowDataQuality ? '' : ` — ${t('closeReason.manager_only')}`)
        }
      >
        {groups.DATA_QUALITY.map((r) => (
          <option key={r.code} value={r.code} disabled={!allowDataQuality}>
            {t(`closeReason.code_${r.code.toLowerCase()}`)}
          </option>
        ))}
      </optgroup>
    </select>
  );
}
